import React, { useState, useEffect } from "react";
import { useStateValue } from "../../../State";
import { types } from "../../../Reducers/actionTypes";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import Languages from "../../../translations";
import SnackbarComponent from "../../../Components/Snackbar";

import LeopsGeneralSection from "./LeopsSupportSections/LeopsGeneralSection";
import LeopsPreviousSupport from "./LeopsSupportSections/LeopsPreviousSupport";
import LeopsPlanAndEvaluation from "./LeopsSupportSections/LeopsPlanAndEvaluation";
import LeopsDecision from "./LeopsSupportSections/LeopsDecision";
import LeopsParentsHeard from "./LeopsSupportSections/LeopsParentsHeard";
import LeopsFollowUp from "./LeopsSupportSections/LeopsFollowUp";

/**
 * Loads the Leops support plan data for the given child via the dedicated
 * combined-plan endpoint and renders the ChildSupportPlan content inline.
 */
const CombinedPlanLeopsSupport = ({ child }) => {
  const { state, actions } = useStateValue();

  const combinedPlanId = child?.CombinedPlanId ?? null;
  const leopsId = child?.LeopsId ?? null;
  const leopsState = state.leops.state;
  const generalPart = state.leops.GeneralPart;
  const loadedLeopsId =
    generalPart && !Array.isArray(generalPart) ? generalPart.Id : null;

  const [SavingData, setSavingData] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState(null);

  useEffect(() => {
    if (combinedPlanId == null) return;

    if (leopsState === "INITIAL_STATE") {
      actions.triggerLoadCombinedPlanLeops(combinedPlanId);
    } else if (leopsState === types.COMBINED_PLAN_LEOPS_LOAD_OK) {
      actions.triggerLeopsHandled();
    } else if (
      leopsState === types.LEOPS_HANDLED &&
      loadedLeopsId !== leopsId
    ) {
      actions.triggerLoadCombinedPlanLeops(combinedPlanId);
    }
  }, [leopsState, loadedLeopsId, leopsId, combinedPlanId, actions]);

  useEffect(() => {
    if (!leopsState) return;

    switch (leopsState) {
      case types.SUPPORTFORM_SAVED_OK:
        setSavingData(false);
        setShowSnackbar(true);
        setSnackbarText(Languages.formSaved);
        actions.triggerLeopsHandled();
        break;
      case types.SUPPORTFORM_SAVED_FAILED:
        setSavingData(false);
        setShowSnackbar(true);
        setSnackbarText(Languages.requestFailed);
        actions.triggerLeopsHandled();
        break;
      default:
        break;
    }
  }, [leopsState]);

  if (combinedPlanId == null || leopsId == null) {
    return null;
  }

  if (leopsState === types.LEOPS_LOAD_FAILED && loadedLeopsId !== leopsId) {
    return (
      <Typography sx={{ textAlign: "center", mt: 3 }}>
        {Languages.requestFailed + ": " + state.leops.Code}
      </Typography>
    );
  }

  const supportData = state.leops.SupportData;
  const ready =
    leopsState !== "INITIAL_STATE" &&
    loadedLeopsId === leopsId &&
    supportData != null &&
    supportData.SupportPlanModel != null;

  if (!ready) {
    return (
      <div style={{ position: "relative", minHeight: 120 }}>
        <Backdrop open={true} sx={{ position: "absolute" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    );
  }

  const supportPlanModel = supportData.SupportPlanModel;
  const leopsPart = supportData.LeopsPart;
  const combinedPlanData = state.combinedPlan.data;
  const parentPersonId = combinedPlanData?.UserPersonId;

  const handleSaveHearing = (Parent, Status, ApprovalText, ParentCount) => {
    const LockingData = {
      LeopsId: generalPart.Id,
      FormId: supportPlanModel.Id,
      Parent: Parent,
      Status: Status,
      ApprovalText: ApprovalText,
      ParentCount: ParentCount,
    };
    actions.triggerSaveSupportForm(LockingData);
    setSavingData(true);
  };

  return (
    <div className="LeopsSupportPart">
      {SavingData && (
        <Backdrop open={true} style={{ color: "#fff", zIndex: 1300 }}>
          <CircularProgress />
        </Backdrop>
      )}

      <h5 className="textCenter">{Languages.ChildSupportPlan}</h5>

      <LeopsGeneralSection
        generalPart={generalPart}
        formGeneralPart={supportData.FormGeneralPart}
        childName={child.Name}
        SSN={child.SSN}
      />
      <LeopsPreviousSupport rows={leopsPart?.PreviousSupport} />
      <LeopsPlanAndEvaluation rows={leopsPart?.PlanAndEvaluation} />
      <LeopsDecision supportPlanModel={supportPlanModel} />
      <LeopsParentsHeard
        supportPlanModel={supportPlanModel}
        generalPart={generalPart}
        parentId={parentPersonId}
        formLocked={false}
        onSave={handleSaveHearing}
      />
      <LeopsFollowUp supportPlanModel={supportPlanModel} />

      {showSnackbar && (
        <SnackbarComponent
          verticalPosition={"bottom"}
          show={showSnackbar}
          okColor={true}
          message={snackbarText}
          callback={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default CombinedPlanLeopsSupport;
